<script setup>
import { computed } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompanySortMenu from './components/CompanySortMenu.vue';
import CompanyFilterMenu from './components/CompanyFilterMenu.vue';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  activeSort: { type: String, default: 'name' },
  activeOrdering: { type: String, default: '' },
  activeFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'search',
  'update:sort',
  'add',
  'update:filters',
  'clearFilters',
]);

const hasActiveFilters = computed(() => {
  return Object.values(props.activeFilters).some(v => v && v.length > 0);
});
</script>

<template>
  <header class="sticky top-0 z-10">
    <div
      class="flex items-start sm:items-center justify-between w-full py-5 px-6 gap-2 border-b border-n-weak bg-n-background"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('COMPANIES.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center flex-shrink-0 gap-4">
          <div class="flex items-center gap-2">
            <CompanyFilterMenu
              :active-filters="activeFilters"
              @update:filters="emit('update:filters', $event)"
              @clear="emit('clearFilters')"
            />
            <CompanySortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
          </div>
          <div class="w-px h-4 bg-n-strong" />
          <Button
            icon="i-lucide-plus"
            :label="$t('COMPANIES.HEADER_BTN_TXT')"
            color="blue"
            size="sm"
            @click="emit('add')"
          />
        </div>
      </div>
    </div>
    <!-- Active Filters Preview -->
    <div v-if="hasActiveFilters" class="px-6 py-3 border-b border-n-weak">
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-xs text-n-slate-11">
          {{ $t('COMPANIES_FILTERS.ACTIVE_FILTERS') }}:
        </span>
        <template v-for="(values, key) in activeFilters" :key="key">
          <span
            v-for="value in values"
            :key="`${key}-${value}`"
            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-blue-3 text-n-blue-11"
          >
            {{ value }}
            <button
              class="hover:text-n-blue-12"
              @click="
                emit('update:filters', {
                  ...activeFilters,
                  [key]: activeFilters[key].filter(v => v !== value),
                })
              "
            >
              <Icon icon="i-lucide-x" class="size-3" />
            </button>
          </span>
        </template>
        <button
          class="text-xs text-n-slate-11 hover:text-n-slate-12 underline"
          @click="emit('clearFilters')"
        >
          {{ $t('COMPANIES_FILTERS.CLEAR_ALL') }}
        </button>
      </div>
    </div>
  </header>
</template>
